
import Dropzone from 'dropzone';
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { WindowType } from '../../util';
import { xsrfheader } from '../../xsrf';
import { ProblemDetails, StatusMessage } from '../backend/backendapi';

declare var window: WindowType;

Dropzone.autoDiscover = false;

/**
 * How to use:
 * Add @uploaded event to the usage of this component. This event argument is a StatusMessage or FileStatusMessage.
 */

@Component({
  name: 'DropZone',
  components: {
    Dropzone,
  },
})
export default class DropZone extends Vue {
  @Prop({ required: true })
  readonly formId!: string;
  @Prop({ default: window.application.baseUrl + 'manager/api/media/upload' })
  readonly url!: string;
  @Prop({ default: 'default-media-upload-template' })
  readonly previewTemplateId!: string;
  @Prop({ default: '' })
  readonly paramInput!: string;
  @Prop({ default: '' })
  readonly paramValue!: string;
  @Prop({ default: '' })
  readonly paramInput2!: string;
  @Prop({ default: '' })
  readonly paramValue2!: string;

  private dropzone: Dropzone | null = null;
  isParamSet: boolean = false; // used for https://github.com/dropzone/dropzone/wiki/FAQ#how-to-submit-additional-data-along-the-file-upload
  isParamSet2: boolean = false;

  mounted(): void {
    if (!this.dropzone) {
      this.init();
    }
  }

  private init(): void {
    if (this.paramInput !== '') this.isParamSet = true;
    if (this.paramInput2 !== '') this.isParamSet2 = true;
    const selector = '#' + this.formId;
    const headers = {};
    headers[xsrfheader().split(':')[0].trim()] = xsrfheader().split(':')[1].trim();
    const config = {
      maxFilesize: 3000,
      paramName: 'Uploads',
      url: this.url,
      headers: headers,
      thumbnailWidth: 70,
      thumbnailHeight: 70,
      previewsContainer: selector + ' .media-list',
      previewTemplate: document.querySelector('#' + this.previewTemplateId)?.innerHTML,
      uploadMultiple: true,
      autoDiscover: false,
      init: () => {
        console.log('dropzone init');
      },
    };

    const dz = new Dropzone(selector + ' form', config);
    dz.on('addedfile', (file: Dropzone.DropzoneFile) => this.$emit('file-added', file));
    dz.on('removedfile', (file: Dropzone.DropzoneFile) => this.$emit('file-removed', file));
    dz.on('error', (file: Dropzone.DropzoneFile, error: string | Error) => this.$emit('error', file, error));
    dz.on('complete', (file: Dropzone.DropzoneFile) => {
      this.$emit('complete', file);
      if (file.status !== 'success' && file.xhr && file.xhr.responseText !== '') {
        const status = JSON.parse(file.xhr.responseText) as (StatusMessage | ProblemDetails);
        const element = file.previewElement.querySelector('[data-dz-errormessage]') as (HTMLElement | null);
        if (element) {
          let messages = [
            status.body,
            (status as ProblemDetails).title,
            (status as ProblemDetails).detail,
          ].filter(e => !!e);
          messages ||= [this.$t('txtError').toString()]; //add "Error" if messages empty
          element.innerText = messages.join(': ');
        }
      }
      if (file.status === 'success' && file.xhr && file.xhr.responseText !== '') {
        const status = JSON.parse(file.xhr.responseText) as StatusMessage;
        this.$emit('uploaded', status);
      }
    });
    dz.on('queuecomplete', (file) => this.$emit('queue-complete', file));
    this.dropzone = dz;
  }

  /**
   * Register your events directly.
   * @param event the name of the event
   * @param cb the callback to call; file argument may not be present of some events
   */
  on(event: string, cb: (file?: Dropzone.DropzoneFile) => void): void {
    this.dropzone?.on(event, cb);
  }

  removeFile(file: Dropzone.DropzoneFile): void {
    this.dropzone?.removeFile(file);
  }
}
